import { Component, Input, OnInit } from '@angular/core';

import { AbstractFilterTypeComponent } from '../../../../Common/Filter/models/abstract-filter-type.component';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { IncludeExcludeEnum } from '../../../../Common/Filter/enums/include-exclude.enum';
import {
    AgentFilterAdHocOption,
    AgentFilterOption,
    AgentFilterSelection,
    AgentFilterSourceOption,
    AgentsFilterModel
} from './agents-filter.model';

/** The groups an agent opens up into, each of which can be ticked and expanded as a whole */
export enum AgentFilterGroupEnum {
    liveSources = 'liveSources',
    adHocRuns = 'adHocRuns'
}

/**
 * Center panel of the Agents filter: a searchable tree of agents, each opening up into its Live
 * Sources (Other among them) and its Ad Hoc Events. Ticking an agent ticks everything under it.
 */
@Component({
    selector: 'agpr-agents-filter',
    templateUrl: './agents-filter.component.html',
    styleUrls: [
        '../../../../Common/Filter/components/filterCheckboxes/filter-checkboxes.component.scss',
        './agents-filter.component.scss'
    ],
    providers: [
        {
            provide: AbstractFilterTypeComponent,
            useExisting: AgentsFilterComponent
        }
    ],
    standalone: false
})
export class AgentsFilterComponent extends AbstractFilterTypeComponent implements OnInit {
    @Input() declare filter: AgentsFilterModel;

    readonly buttonTypes = ButtonTypesEnum;
    readonly severity = messageSeverityEnum;
    readonly groups = AgentFilterGroupEnum;

    searchQuery: string = '';
    filteredOptions: AgentFilterOption[] = [];

    /** The selection being edited, by agent. An agent missing from here has nothing ticked */
    selections: Map<number, AgentFilterSelection> = new Map<number, AgentFilterSelection>();

    /** Agents, and groups within them, that have been opened - everything starts closed */
    private expanded: Set<string> = new Set<string>();

    // Created by CustomFilterWrapperComponent, which sets the filter on the instance rather than
    // binding it, so there are no input changes to react to
    ngOnInit(): void {
        this.refresh();
    }

    refresh(): void {
        this.selections = new Map(AgentsFilterModel.cloneSelections(this.filter.getCurrentValue())
            .map(selection => [selection.agentId, selection]));
        this.search();
        this.seedScopedAgent();
    }

    get anyOptionsSelected(): boolean {
        return this.selections.size > 0;
    }

    /** The search is one agent's coverage, so that agent stays ticked and only what's under it changes */
    get isScoped(): boolean {
        return this.filter.scopedAgentId !== null;
    }

    /** Other can't be excluded on its own, so excluding lists everything but it - see AgentsFilterModel.toQuery */
    get isExcluding(): boolean {
        return this.filter.currentIncludeExclude === IncludeExcludeEnum.exclude;
    }

    /** Whether the agent has anything to open up into - excluding has no Other to fall back on */
    hasChildren(option: AgentFilterOption): boolean {
        return !this.isExcluding || option.sources.length > 0 || option.adHocRuns.length > 0;
    }

    search(): void {
        const query = this.searchQuery?.toLocaleLowerCase() ?? '';

        this.filteredOptions = this.filter.visibleOptions.filter(option => option.name.toLocaleLowerCase().includes(query));
    }

    /** Spec: select all takes in the agents on display, leaving any hidden by the search as they were */
    selectItems(): void {
        if (this.anyOptionsSelected) {
            this.selections.clear();
        } else {
            this.filteredOptions.forEach(option => this.selections.set(option.id, AgentsFilterModel.fullSelection(option)));
        }

        this.handleChange();
    }

    isExpanded(option: AgentFilterOption, group?: AgentFilterGroupEnum): boolean {
        return this.expanded.has(this.expandKey(option, group));
    }

    toggleExpanded(option: AgentFilterOption, group?: AgentFilterGroupEnum): void {
        const key = this.expandKey(option, group);

        if (!this.expanded.delete(key)) {
            this.expanded.add(key);
        }
    }

    isAgentSelected(option: AgentFilterOption): boolean {
        const selection = this.selections.get(option.id);

        return !!selection && this.filter.isFullSelection(selection);
    }

    isAgentPartial(option: AgentFilterOption): boolean {
        return this.selections.has(option.id) && !this.isAgentSelected(option);
    }

    toggleAgent(option: AgentFilterOption, selected: boolean): void {
        if (selected) {
            this.selections.set(option.id, AgentsFilterModel.fullSelection(option));
        } else {
            this.selections.delete(option.id);
        }

        this.handleChange();
    }

    isGroupSelected(option: AgentFilterOption, group: AgentFilterGroupEnum): boolean {
        const selection = this.selections.get(option.id);

        return group === AgentFilterGroupEnum.liveSources
            ? this.filter.allLiveSourcesSelected(option, selection)
            : this.filter.allAdHocRunsSelected(option, selection);
    }

    isGroupPartial(option: AgentFilterOption, group: AgentFilterGroupEnum): boolean {
        const selection = this.selections.get(option.id);
        const anySelected = group === AgentFilterGroupEnum.liveSources
            ? !!selection?.other && !this.isExcluding || !!selection?.sourceIds.length
            : !!selection?.adHocIds.length;

        return anySelected && !this.isGroupSelected(option, group);
    }

    toggleGroup(option: AgentFilterOption, group: AgentFilterGroupEnum, selected: boolean): void {
        const selection = this.selectionFor(option);

        if (group === AgentFilterGroupEnum.liveSources) {
            selection.sourceIds = selected ? option.sources.map(source => source.id) : [];
            selection.other = selected;
        } else {
            selection.adHocIds = selected ? option.adHocRuns.map(run => run.id) : [];
        }

        this.narrowed(selection);
    }

    isSourceSelected(option: AgentFilterOption, source: AgentFilterSourceOption): boolean {
        return !!this.selections.get(option.id)?.sourceIds.includes(source.id);
    }

    toggleSource(option: AgentFilterOption, source: AgentFilterSourceOption, selected: boolean): void {
        const selection = this.selectionFor(option);

        selection.sourceIds = selected
            ? [...selection.sourceIds, source.id]
            : selection.sourceIds.filter(id => id !== source.id);

        this.narrowed(selection);
    }

    isOtherSelected(option: AgentFilterOption): boolean {
        return !!this.selections.get(option.id)?.other;
    }

    toggleOther(option: AgentFilterOption, selected: boolean): void {
        const selection = this.selectionFor(option);

        selection.other = selected;

        this.narrowed(selection);
    }

    isAdHocSelected(option: AgentFilterOption, run: AgentFilterAdHocOption): boolean {
        return !!this.selections.get(option.id)?.adHocIds.includes(run.id);
    }

    toggleAdHoc(option: AgentFilterOption, run: AgentFilterAdHocOption, selected: boolean): void {
        const selection = this.selectionFor(option);

        selection.adHocIds = selected
            ? [...selection.adHocIds, run.id]
            : selection.adHocIds.filter(id => id !== run.id);

        this.narrowed(selection);
    }

    override currentIncludeExcludeChange(): void {
        super.currentIncludeExcludeChange();

        // Scoped, the two modes start from opposite ends: everything included, or nothing excluded
        if (this.isScoped) {
            this.selections.clear();
            this.seedScopedAgent();
            this.handleChange();

            return;
        }

        // Other only counts when including. Going to exclude, an agent ticked for Other alone has
        // nothing left; going back, Other follows the agent being ticked whole.
        this.selections.forEach((selection, agentId) => {
            if (this.isExcluding) {
                if (!selection.sourceIds.length && !selection.adHocIds.length && !this.filter.isFullSelection(selection, true)) {
                    this.selections.delete(agentId);
                }
            } else {
                selection.other = this.filter.isFullSelection(selection, true);
            }
        });

        this.handleChange();
    }

    private expandKey(option: AgentFilterOption, group?: AgentFilterGroupEnum): string {
        return group ? `${option.id}:${group}` : `${option.id}`;
    }

    /** An agent with nothing ticked yet starts empty, so ticking one thing under it takes only that */
    private selectionFor(option: AgentFilterOption): AgentFilterSelection {
        if (!this.selections.has(option.id)) {
            this.selections.set(option.id, { agentId: option.id, sourceIds: [], other: false, adHocIds: [] });
        }

        return this.selections.get(option.id);
    }

    /**
     * With nothing left ticked under it the agent would match nothing, so it is unticked as well -
     * or, scoped, where the agent can't be unticked, everything under it is ticked again
     */
    private narrowed(selection: AgentFilterSelection): void {
        if (!(selection.other && !this.isExcluding) && !selection.sourceIds.length && !selection.adHocIds.length) {
            this.selections.delete(selection.agentId);
            this.seedScopedAgent();
        }

        this.handleChange();
    }

    /**
     * Including, the scoped agent starts with everything under it ticked, opened up since narrowing it
     * is all there is to do. Excluding, it starts with nothing ticked - nothing is excluded.
     */
    private seedScopedAgent(): void {
        const option = this.filter.optionFor(this.filter.scopedAgentId);

        if (!option) {
            return;
        }

        this.expanded.add(this.expandKey(option));

        if (!this.isExcluding && !this.selections.has(option.id)) {
            this.selections.set(option.id, AgentsFilterModel.fullSelection(option));
        }
    }

    private handleChange(): void {
        // Kept in the order the agents are listed, so the chips are too. Scoped, the agent ticked
        // whole is the search as it already is, so it adds nothing to the filter.
        const value = this.filter.options
            .filter(option => this.selections.has(option.id))
            .map(option => this.selections.get(option.id))
            .filter(selection => !(this.isScoped && !this.isExcluding && this.filter.isFullSelection(selection)));

        this.filter.setCurrentValue(AgentsFilterModel.cloneSelections(value));
    }
}
